import CorrectRate from "@/pages/progress/correct-rate/correct-rate";
import SettingsPaymentCards from "@/pages/settings/payment/cards";
import { lazy } from "react";
import type { PathRouteProps } from "react-router-dom";

// Public routes - Lazy loaded for better performance
const Login = lazy(() => import("@/pages/login"));
const Welcome = lazy(() => import("@/pages/welcome"));
const Start = lazy(() => import("@/pages/start"));
const Suspended = lazy(() => import("@/pages/suspended"));
const ForgotPassword = lazy(() => import("@/pages/forgot-password"));

// Private routes - Lazy loaded
const Home = lazy(() => import("@/pages/home"));
const DictationCategories = lazy(() => import("@/pages/courses/categories"));
const CourseWordDetail = lazy(() => import("@/pages/course-word/detail"));
const DictationCategoriesLessons = lazy(
  () => import("@/pages/lessons/dictation-categories-lessons"),
);
const DictationCategoryLessonStart = lazy(
  () => import("@/pages/lessons/dictation-category-lesson-start"),
);
const DictationCategoryLessonLearn = lazy(
  () => import("@/pages/lessons/dictation-category-lesson-learn"),
);
const DictationLessons = lazy(() => import("@/pages/lessons/dictation-lessons"));
const DictationLessonStart = lazy(() => import("@/pages/lessons/dictation-lesson-start"));
const DictationLessonLearn = lazy(() => import("@/pages/lessons/dictation-lesson-learn"));
const DictationLessonListen = lazy(() => import("@/pages/lessons/dictation-lesson-listen"));
const DictationCategoryLessonListen = lazy(
  () => import("@/pages/lessons/dictation-category-lesson-listen"),
);
const DictationPartLessons = lazy(() => import("@/pages/lessons/dictation-part-lessons"));
const DictationPartLessonStart = lazy(() => import("@/pages/lessons/dictation-part-lesson-start"));
const DictationPartLessonLearn = lazy(() => import("@/pages/lessons/dictation-part-lesson-learn"));
const DictationPartLessonListen = lazy(
  () => import("@/pages/lessons/dictation-part-lesson-listen"),
);
const WordLessons = lazy(() => import("@/pages/vocabulary"));
const WordLessonDetail = lazy(() => import("@/pages/vocabulary/start"));
const WordLessonLearn = lazy(() => import("@/pages/vocabulary/learn"));
const WordLessonListenCommentary = lazy(() => import("@/pages/vocabulary/listen-commentary"));
const WordLessonListen = lazy(() => import("@/pages/vocabulary/listen"));
const WordResult = lazy(() => import("@/pages/vocabulary/result"));
const ReviewWord = lazy(() => import("@/pages/review-word"));
const ReviewWordLessonStart = lazy(() => import("@/pages/review-word/start"));
const ReviewWordLessonLearn = lazy(() => import("@/pages/review-word/learn"));
const ReviewWordLessonListenCommentary = lazy(
  () => import("@/pages/review-word/listen-commentary"),
);
const ReviewDictation = lazy(() => import("@/pages/review-dictation"));
const ReviewDictationLessonStart = lazy(() => import("@/pages/review-dictation/start"));
const ReviewDictationCategoryLessonLearn = lazy(() => import("@/pages/review-dictation/learn"));
const Profile = lazy(() => import("@/pages/profile"));
const Notification = lazy(() => import("@/pages/notification"));
const Ranking = lazy(() => import("@/pages/ranking"));
const Progress = lazy(() => import("@/pages/progress"));
const Friends = lazy(() => import("@/pages/friends"));
const Wordbook = lazy(() => import("@/pages/wordbook"));
const Settings = lazy(() => import("@/pages/settings"));
const SettingsProfile = lazy(() => import("@/pages/settings/profile"));
const SettingsSpeed = lazy(() => import("@/pages/settings/speed"));
const SettingsReminder = lazy(() => import("@/pages/settings/reminder"));
const SettingsUpdateEmail = lazy(() => import("@/pages/settings/update-email"));
const SettingsPassword = lazy(() => import("@/pages/settings/password"));
const SettingsPayment = lazy(() => import("@/pages/settings/payment"));
const Support = lazy(() => import("@/pages/support"));

/**
 * Public routes - accessible without authentication
 */
export const routes: PathRouteProps[] = [
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/welcome",
    element: <Welcome />,
  },
  {
    path: "/start",
    element: <Start />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/support",
    element: <Support />,
  },
];

/**
 * Private routes - require authentication
 * Organized by feature groups:
 * - Home & Navigation
 * - Courses & Lessons
 * - Word Lessons
 * - Review
 * - User Profile & Social
 */
export const privateRoutes: PathRouteProps[] = [
  // Suspended page (requires auth, but only accessible to paused users)
  {
    path: "/suspended",
    element: <Suspended />,
  },
  // Home & Navigation
  {
    path: "/",
    element: <Home />,
  },
  {
    path: "/home",
    element: <Home />,
  },

  // Courses
  {
    path: "/dictation-categories/:courseId",
    element: <DictationCategories />,
  },
  {
    path: "/courses-word/:courseId",
    element: <CourseWordDetail />,
  },

  // Lessons
  {
    path: "/dictation-categories-lessons/:courseId/:categoryId",
    element: <DictationCategoriesLessons />,
  },
  {
    path: "/dictation-category-lesson/start/:courseId/:categoryId/:lessonId",
    element: <DictationCategoryLessonStart />,
  },
  {
    path: "/dictation-category-lesson/learn/:courseId/:categoryId/:lessonId",
    element: <DictationCategoryLessonLearn />,
  },
  {
    path: "/dictation-lessons/:courseId",
    element: <DictationLessons />,
  },
  {
    path: "/dictation-lesson/start/:courseId/:lessonId",
    element: <DictationLessonStart />,
  },
  {
    path: "/dictation-lesson/learn/:courseId/:lessonId",
    element: <DictationLessonLearn />,
  },
  {
    path: "/dictation-lesson-listen/:courseId/:lessonId",
    element: <DictationLessonListen />,
  },
  {
    path: "/dictation-category-lesson-listen/:courseId/:categoryId/:lessonId",
    element: <DictationCategoryLessonListen />,
  },
  {
    path: "/dictation-part-lessons/:courseId",
    element: <DictationPartLessons />,
  },
  {
    path: "/dictation-part-lesson/start/:courseId/:lessonId",
    element: <DictationPartLessonStart />,
  },
  {
    path: "/dictation-part-lesson/learn/:courseId/:lessonId",
    element: <DictationPartLessonLearn />,
  },
  {
    path: "/dictation-part-lesson-listen/:courseId/:lessonId",
    element: <DictationPartLessonListen />,
  },

  // Word Lessons
  {
    path: "/word-lessons/:courseId/:partId",
    element: <WordLessons />,
  },
  {
    path: "/word-lessons/start/:lessonId/:courseId/:partId",
    element: <WordLessonDetail />,
  },
  {
    path: "/word-lessons/learn/:lessonId/:courseId/:partId",
    element: <WordLessonLearn />,
  },
  {
    path: "/word-lessons/listen-commentary",
    element: <WordLessonListenCommentary />,
  },
  {
    path: "/word-lessons/listen/:lessonId/:courseId/:partId",
    element: <WordLessonListen />,
  },
  {
    path: "/word-lessons/result",
    element: <WordResult />,
  },

  // Review
  {
    path: "/review-word",
    element: <ReviewWord />,
  },
  {
    path: "/review-word/start/:courseId/:partId",
    element: <ReviewWordLessonStart />,
  },
  {
    path: "/review-word-lessons/learn/:courseId/:partId",
    element: <ReviewWordLessonLearn />,
  },
  {
    path: "/review-word-lessons/listen-commentary/:courseId/:partId",
    element: <ReviewWordLessonListenCommentary />,
  },
  {
    path: "/review-dictation",
    element: <ReviewDictation />,
  },
  {
    path: "/review-dictation/start/:courseId/:categoryId",
    element: <ReviewDictationLessonStart />,
  },
  {
    path: "/review-dictation/learn/:courseId/:categoryId",
    element: <ReviewDictationCategoryLessonLearn />,
  },

  // User Profile & Social
  {
    path: "/profile",
    element: <Profile />,
  },
  {
    path: "/notifications",
    element: <Notification />,
  },
  {
    path: "/rankings",
    element: <Ranking />,
  },
  {
    path: "/progress",
    element: <Progress />,
  },
  {
    path: "/progress/correct-rate",
    element: <CorrectRate />,
  },
  {
    path: "/friends",
    element: <Friends />,
  },
  {
    path: "/wordbook",
    element: <Wordbook />,
  },
  {
    path: "/settings",
    element: <Settings />,
  },
  {
    path: "/settings/profile",
    element: <SettingsProfile />,
  },
  {
    path: "/settings/speed",
    element: <SettingsSpeed />,
  },
  {
    path: "/settings/reminder",
    element: <SettingsReminder />,
  },
  {
    path: "/settings/update-email",
    element: <SettingsUpdateEmail />,
  },
  {
    path: "/settings/password",
    element: <SettingsPassword />,
  },
  {
    path: "/settings/payment",
    element: <SettingsPayment />,
  },
  {
    path: "/settings/payment/cards",
    element: <SettingsPaymentCards />,
  },
];
